/**
 * Mock data for driver-facing screens.
 * Used as fallback when the backend API is unavailable.
 * Training quizzes and checklists match JotForm source templates.
 */

import { JobStatus } from '../types/job';

export interface MockJob {
  id: string;
  productTitle: string;
  productCategory: string;
  categorySlug?: string;
  pickupAddress: string;
  deliveryAddress: string;
  pickupLat: number;
  pickupLng: number;
  deliveryLat: number;
  deliveryLng: number;
  sellerName: string;
  sellerPhone: string;
  buyerName: string;
  buyerPhone: string;
  payoutAmount: number;
  _distanceMiles: number;
  _locked?: boolean;
  status: JobStatus;
  deliveryWindows: { id: string; startTime: string; endTime: string }[];
  selectedWindow?: { startTime: string; endTime: string };
}

/** Approved certification category slugs for the current driver */
// Empty by default — new drivers must complete General Driver Certification first
export const MOCK_CERTIFICATIONS: string[] = [];

const tomorrow = new Date();
tomorrow.setDate(tomorrow.getDate() + 1);
const dayAfter = new Date();
dayAfter.setDate(dayAfter.getDate() + 2);

function window(daysOut: number, startHour: number, endHour: number) {
  const d = new Date();
  d.setDate(d.getDate() + daysOut);
  const s = new Date(d); s.setHours(startHour, 0, 0, 0);
  const e = new Date(d); e.setHours(endHour, 0, 0, 0);
  return { id: `w-${daysOut}-${startHour}`, startTime: s.toISOString(), endTime: e.toISOString() };
}

// These are kept only as silent fallback — the app should always try real WooCommerce data first
export const MOCK_AVAILABLE_JOBS: MockJob[] = [];

export const MOCK_ACTIVE_JOB: MockJob | null = null;

export const MOCK_EARNINGS = {
  todayEarnings: 0,
  weekEarnings: 0,
  pendingBalance: 0,
  totalEarnings: 0,
  completedJobs: 0,
  avgRating: 0,
  history: [] as any[],
};

export const MOCK_DRIVER = {
  id: 'drv-001',
  name: '',
  email: '',
  phone: '',
  status: 'REGISTERED',
  vehicleType: '',
  vehicleDetails: '',
  insuranceProvider: '',
  workingRadiusMiles: 200,
  baseLat: 40.7128,
  baseLng: -74.0060,
  baseAddress: '',
  profilePhotoUrl: null,
  certifications: [] as any[],
};

// ─── JotForm-Accurate Training Courses ────────────────────────────────────────
// Source: https://form.jotform.com/243605695937167

export type EquipmentType = 'peloton-bike' | 'peloton-bike-plus' | 'peloton-tread' | 'peloton-tread-plus' | 'peloton-row';

export const EQUIPMENT_OPTIONS = [
  { value: 'peloton-bike', label: 'Peloton Bike (Regular)', videoUrl: 'https://youtube.com/watch?v=7cb7zc-eoWQ' },
  { value: 'peloton-bike-plus', label: 'Peloton Bike+', videoUrl: 'https://youtube.com/watch?v=mx83vZZxIyo' },
  { value: 'peloton-tread', label: 'Peloton Tread', videoUrl: 'https://youtube.com/watch?v=dGGJc6XchZU' },
  { value: 'peloton-tread-plus', label: 'Peloton Tread+', videoUrl: 'https://youtube.com/watch?v=GONcOJ55P0Q' },
  { value: 'peloton-row', label: 'Peloton Row', videoUrl: null },
];

/** Equipment-specific instructional content shown conditionally */
export const EQUIPMENT_INSTRUCTIONS: Record<EquipmentType, string> = {
  'peloton-bike': `Peloton Bike (Regular) – Delivery & Setup Instructions

• Weight: ~135 lbs (frame + screen)
• Requires 2 people for safe lifting
• Screen is fragile — always transport face-up with protective padding
• Use the included allen wrench kit for pedal installation
• Stabilizer feet must be adjusted until the bike does not rock
• Connect power cable and pair via Bluetooth for initial setup
• Walk the customer through the basic ride start process`,

  'peloton-bike-plus': `Peloton Bike+ – Delivery & Setup Instructions

• Weight: ~140 lbs
• 23.8" rotating HD touchscreen — handle with extreme care
• Screen rotates 180° for off-bike workouts — verify rotation clearance
• Auto-follow resistance feature — pair with customer's account during setup
• Requires stable power outlet within 6 feet
• Ensure WiFi connection is strong at placement location
• Walk customer through screen rotation and streaming features`,

  'peloton-tread': `Peloton Tread – Delivery & Setup Instructions

• Weight: ~290 lbs — requires 2-person carry minimum
• Measure ALL doorways, stairways, and hallways before attempting delivery
• Belt must be centered and tensioned after placement
• Safety key must be attached and tested
• Plug into a dedicated 15A circuit (no extension cords)
• Run a short calibration walk cycle
• Show customer emergency stop and speed controls`,

  'peloton-tread-plus': `Peloton Tread+ – Delivery & Setup Instructions

• Weight: ~455 lbs — MUST have professional moving equipment
• Slat belt system — do NOT fold or bend slats during transport
• Requires dedicated 20A circuit — verify with customer before delivery
• Minimum ceiling clearance: user height + 20 inches
• Safety key is mandatory — test dead-man switch before leaving
• Full safety briefing required: children/pet lockout, speed limits
• Walk through all safety features with customer and have them sign off`,

  'peloton-row': `Peloton Row – Delivery & Setup Instructions

• Weight: ~156 lbs
• 23.8" HD touchscreen — same fragility precautions as Bike+
• Requires 8' × 4' floor space minimum
• Form Assist camera must have clear line of sight — no mirrors behind
• Level the unit on all four contact points
• Strap tension adjustment — show customer proper technique
• Walk customer through seated position, stroke technique on screen`,
};

/** Equipment-specific quiz questions (5 per equipment type) — from JotForm form.jotform.com/243605695937167 */
export const EQUIPMENT_QUIZ_QUESTIONS: Record<EquipmentType, { id: string; questionText: string; options: string[]; correctIndex: number }[]> = {
  'peloton-bike': [
    { id: 'bike-q1', questionText: 'Before moving a Peloton Bike, which of the following should you do first?', options: ['Test the touchscreen', 'Power off and unplug the bike', 'Remove the pedals', 'Lower the seat height'], correctIndex: 0 },
    { id: 'bike-q2', questionText: 'What should you do with the touchscreen before moving the Peloton Bike?', options: ['Wrap it in plastic wrap', 'Remove it and store it securely', 'Tape it to the bike frame', 'Leave it as it is'], correctIndex: 0 },
    { id: 'bike-q3', questionText: 'When moving the Peloton Bike down the stairs, which part should face downward?', options: ['The handlebars for better grip', 'The flywheel because it is heavier and provides stability', 'The seat for easier tilting', 'The touchscreen mount to protect it'], correctIndex: 1 },
    { id: 'bike-q4', questionText: 'What tool is required to remove the safety pin from the Peloton Bike?', options: ['Phillips head screwdriver', 'Allen key', 'Flathead screwdriver', 'Wrench'], correctIndex: 0 },
    { id: 'bike-q5', questionText: 'What is a key step to protect the Peloton Bike during vehicle transport?', options: ['Secure it with straps, place it flat on its side, and cover it with blankets', 'Remove the pedals for extra space', 'Remove the chain', 'Stack other items on top'], correctIndex: 0 },
  ],
  'peloton-bike-plus': [
    { id: 'bikeplus-q1', questionText: 'What should you remove from the Bike+ before moving it?', options: ['Pedals', 'Touchscreen', 'Flywheel', 'Resistance knob'], correctIndex: 1 },
    { id: 'bikeplus-q2', questionText: 'What is the correct way to tilt the Bike+ forward onto its transport wheels for moving?', options: ['Lift the rear stabilizer and gently tilt the bike forward onto the wheels.', 'Push the handlebars forward while keeping the bike upright.', 'Tilt the bike forward to engage the wheels.', 'Lift the flywheel and roll the bike backward.'], correctIndex: 0 },
    { id: 'bikeplus-q3', questionText: 'What feature on the Bike+ can make it easier to plug in the power cord?', options: ['A retractable power cord', 'A rubber piece near the power socket that can be removed', 'A built-in cord guide', 'An automatic cord reel'], correctIndex: 1 },
    { id: 'bikeplus-q4', questionText: 'When moving the Bike+ down the stairs, which part should face downward for safety?', options: ['The handlebars for better grip', 'The flywheel because it is heavier and provides stability', 'The touchscreen mount to protect it', 'The seat for easier tilting'], correctIndex: 1 },
    { id: 'bikeplus-q5', questionText: 'What tool is required to remove the safety pin from the Bike+?', options: ['Allen key', 'Phillips head screwdriver', 'Flathead screwdriver', 'Wrench'], correctIndex: 0 },
  ],
  'peloton-tread': [
    { id: 'tread-q1', questionText: 'What incline setting should the Tread be adjusted to before moving it?', options: ['0%', '1%', '3%', '5%'], correctIndex: 2 },
    { id: 'tread-q2', questionText: 'Where is the power button on the Peloton Tread located, and why is it critical to be cautious around it during a move?', options: ['At the base of the Tread, it must be protected to avoid damage.', 'On the handrail, it should be turned off.', 'Under the incline motor, it must be removed before moving.', 'On the touchscreen, it should be used to reset the machine.'], correctIndex: 0 },
    { id: 'tread-q3', questionText: 'Why is it important not to drag the Peloton Tread when moving it?', options: ['Dragging can damage the power button at the base, preventing the Tread from turning on or functioning properly.', 'Dragging will void the warranty.', 'Dragging can interfere with the incline mechanism.', 'All of the above.'], correctIndex: 0 },
    { id: 'tread-q4', questionText: 'What is the proper way to move the Peloton Tread on the same floor?', options: ['Tilt it forward onto its wheels and roll it carefully.', 'Drag it slowly across the floor.', 'Lift it from the middle of the belt.', 'Push it backward with the incline set to 0%.'], correctIndex: 0 },
    { id: 'tread-q5', questionText: 'What safety precautions should you take when placing the Tread in its new location?', options: ['Ensure the power button is not obstructed.', 'Check for a stable and level surface.', 'Verify enough clearance around the machine for safe use.', 'All of the above.'], correctIndex: 3 },
  ],
  'peloton-tread-plus': [
    { id: 'treadplus-q1', questionText: 'What incline setting should the Tread+ be adjusted to before moving it?', options: ['0%', '3%', '10%', 'Doesn\'t matter'], correctIndex: 1 },
    { id: 'treadplus-q2', questionText: 'Where are the handles located on the Tread+, and what challenges do they present during a move?', options: ['On the touchscreen mount, they are easy to grip for tilting the machine.', 'On the handrails, they provide stability for lifting.', 'Underneath the base, they are small and difficult to hold, making it challenging to lift and maneuver the Tread+.', 'Near the incline motor, they are designed to roll the machine effortlessly.'], correctIndex: 2 },
    { id: 'treadplus-q3', questionText: 'Why are the handles underneath the Tread+ a concern for moving the equipment?', options: ['Their small size makes it hard to get a secure grip, increasing the risk of dropping the machine.', 'They are prone to breaking if used improperly.', 'They are located too close to the power button.', 'They require specialized tools for safe handling.'], correctIndex: 0 },
    { id: 'treadplus-q4', questionText: 'What should you avoid doing with the Tread+ during a move?', options: ['Dragging it across the floor or stairs.', 'Tilting it without using the proper handles.', 'Attempting to move it alone.', 'All of the above.'], correctIndex: 3 },
    { id: 'treadplus-q5', questionText: 'How can you secure the Tread+ for safe transport in a vehicle?', options: ['Use strong straps to anchor it securely and cover it with thick padding. Lay it flat with the arms and handrail on top to make sure everything is level.', 'Fold it in half to reduce its size.', 'Place it flat on its side with no padding.', 'Use bungee cords to secure it to the walls of the truck.'], correctIndex: 0 },
  ],
  'peloton-row': [
    { id: 'row-q1', questionText: 'What should you do with the Row before moving it?', options: ['Fold the screen into the base', 'Fold the machine in half', 'Remove the rowing handle', 'Power it on'], correctIndex: 0 },
    { id: 'row-q2', questionText: 'How can the Row be moved on the same floor?', options: ['Roll it using its built-in wheels', 'Push it without tilting', 'Carry it by the rail', 'Drag it across the floor'], correctIndex: 0 },
    { id: 'row-q3', questionText: 'What should you do before moving the Row through narrow spaces or doorways?', options: ['Measure the doorway and tilt the Row carefully to fit', 'Disassemble the rail first', 'Remove the monitor and carry it separately', 'Push it straight through without tilting'], correctIndex: 1 },
    { id: 'row-q4', questionText: 'What is an important factor to check before placing the Row in its new location?', options: ['Ceiling height', 'Flooring material', 'Clearance for the rowing motion', 'Wi-Fi connection strength'], correctIndex: 2 },
    { id: 'row-q5', questionText: 'How should the Row be secured for transport?', options: ['Strap it and cover it with padding', 'Place it upside down', 'Fold it and lay it flat', 'Use bubble wrap only'], correctIndex: 0 },
  ],
};

/** Customer service standards quiz questions (5 questions, same for all equipment) */
export const SERVICE_QUIZ_QUESTIONS = [
  { id: 'svc-q1', questionText: 'What should you do if you arrive and the customer is not home?', options: ['Leave the equipment at the door', 'Wait 15 minutes then call the customer', 'Leave immediately', 'Contact Commonplace support and wait for instructions'], correctIndex: 3 },
  { id: 'svc-q2', questionText: 'How should you handle a customer complaint about a scratch on the equipment?', options: ['Deny responsibility', 'Document it with photos and report to Commonplace immediately', 'Offer a personal discount', 'Ignore it if it\'s minor'], correctIndex: 1 },
  { id: 'svc-q3', questionText: 'What is the correct way to communicate delivery delays?', options: ['Don\'t say anything until you arrive', 'Text the customer directly from your personal phone', 'Use the Commonplace app messaging system to notify all parties', 'Post on social media'], correctIndex: 2 },
  { id: 'svc-q4', questionText: 'When is it appropriate to share your personal phone number with a customer?', options: ['Always, for better communication', 'Only for repeat customers', 'Never — all communication goes through the Commonplace app', 'Only if they ask nicely'], correctIndex: 2 },
  { id: 'svc-q5', questionText: 'What should you wear on delivery day?', options: ['Whatever you want', 'Clean, professional attire with closed-toe shoes', 'A company uniform', 'Workout clothes since you\'ll be lifting'], correctIndex: 1 },
];

export const TRAINING_CATEGORIES = [
  { key: 'all', label: 'All' },
  { key: 'driver-cert', label: 'Driver Certification', slugs: ['driver-certification'] },
  { key: 'palletizing', label: 'Freight', slugs: ['palletizing'] },
  { key: 'peloton', label: 'Peloton', slugs: ['fitness-equipment'] },
  { key: 'strength', label: 'Strength', slugs: ['tonal'] },
  { key: 'treadmills', label: 'Treadmills', slugs: ['treadmill'] },
  { key: 'rowers', label: 'Rowers', slugs: ['rowing-machine'] },
  { key: 'wellness', label: 'Recovery / Wellness', slugs: ['sauna', 'hot-tub'] },
  // 'general' removed — covered by driver-certification
  { key: 'appliance', label: 'Appliances', slugs: ['appliance'] },
  { key: 'cold-plunge', label: 'Cold Plunge', slugs: ['cold-plunge'] },
  { key: 'outdoor', label: 'Outdoor', slugs: ['outdoor'] },
  { key: 'furniture', label: 'Furniture', slugs: ['furniture'] },
  { key: 'gym-equipment', label: 'Gym Equipment', slugs: ['gym-equipment'] },
  { key: 'electronics', label: 'Electronics', slugs: ['electronics'] },
  { key: 'golf-simulator', label: 'Golf Simulators', slugs: ['golf-simulator'] },
  { key: 'pool-table', label: 'Game Room', slugs: ['pool-table'] },
  { key: 'massage-chair', label: 'Massage Chairs', slugs: ['massage-chair'] },
];

export const MOCK_COURSES = [
  {
    id: 'course-driver-cert',
    title: 'General Driver Certification',
    categorySlug: 'driver-certification',
    _sortOrder: 0,
    status: 'PUBLISHED',
    videoUrl: null,
    textContent: `This mandatory course covers the foundational knowledge every Commonplace driver must have before taking on any delivery.

You will learn:
• Vehicle safety inspection and pre-trip checks
• Proper loading and securing techniques for heavy equipment
• Customer communication standards and professionalism
• Delivery workflow from job acceptance to completion
• Incident reporting and damage documentation procedures

After completing the course material, you must pass the certification quiz with a score of 80% or higher. This course must be completed before you can access any equipment-specific certifications.`,
    passingScore: 80,
    _count: { quizQuestions: 5 },
    quizQuestions: [
      { id: 'dcert-q1', questionText: 'What should you do on your way to pickup and delivery?', options: ['Wait until you arrive to contact the seller and customer', 'Update the seller as you are on your way and update the customer with the ETA after pickup', 'Use humor and informal language to engage customers', 'Contact the seller and customer only if there is a delay'], correctIndex: 1 },
      { id: 'dcert-q2', questionText: 'Which of the following is NOT part of good customer service?', options: ['Be polite, professional, and punctual', 'Communicate clearly with customers', 'Make unauthorized promises to satisfy customer demands', 'Respect customer preferences, privacy, and house rules'], correctIndex: 2 },
      { id: 'dcert-q3', questionText: 'What should you do to ensure delivery excellence?', options: ['Rush deliveries to meet tight deadlines', 'Assemble and verify the proper functioning of delivered items', 'Leave packaging and debris after delivery', 'Handle items without protective equipment'], correctIndex: 1 },
      { id: 'dcert-q4', questionText: 'Which behavior is considered unprofessional conduct?', options: ['Using polite and professional language', 'Refraining from making unauthorized modifications', 'Arguing with customers or dismissing concerns', 'Respecting customer preferences'], correctIndex: 2 },
      { id: 'dcert-q5', questionText: 'What action should be avoided to prevent delivery negligence?', options: ['Inspect items for damage before delivery', 'Compromise on safety to deliver faster', 'Use protective equipment when handling items', 'Clean up packaging and leave the space tidy'], correctIndex: 1 },
    ],
  },
  {
    id: 'course-peloton-bike',
    title: 'Peloton Bike (Regular)',
    categorySlug: 'peloton-bike',
    defaultEquipment: 'peloton-bike',
    _sortOrder: 2,
    status: 'PUBLISHED',
    videoUrl: 'https://youtube.com/watch?v=7cb7zc-eoWQ',
    textContent: `Peloton Bike (Regular) — Pickup & Delivery Guide

WATCH THE DISASSEMBLY VIDEO BEFORE YOU START — it covers the full process step by step.

TOOLS NEEDED:
• Phillips head screwdriver to remove the safety key and screen

PRE-PICKUP INSPECTION:
• Ask the seller about stairs and arrange help if needed. The flywheel side of the bike should face the bottom of the stairs when carrying.
• Test the screen: Tap the WiFi icon in the top right corner → try connecting to WiFi so the keyboard pops up → make sure the keyboard and touch screen work properly.
• Test ride the bike: If logged out, go to "Get Started" → choose language → select "Sample Class" or "Just Ride". Verify: (1) Resistance adjusts smoothly with the knob and shows on screen, (2) Cadence metric works when you pedal, (3) Sound works properly.
• Sit on the bike, pedal, and adjust resistance. Make sure pedals and flywheel are not making any unusual noises.
• Ask the seller how many rides are on the bike and if there are any known issues with the equipment.
• If the bike is dusty or sweaty, ask the seller for a wet paper towel and wipe it down so it's clean for delivery. Buyers complain about cleanliness.

DISASSEMBLY STEPS:
1. Remove the safety screw from under the handlebar stem
2. Unplug the cables from the back of the screen
3. Remove the handlebars with the screen from the bike frame
4. Tuck the loose cables into the open hole where the handlebars were

DELIVERY & SETUP:
• Safe transport of the 135 lb unit (requires 2 people)
• Screen protection during transport — always face-up with padding
• Pedal installation using the included allen wrench kit
• Stabilizer feet adjustment until bike doesn't rock
• Power cable connection and WiFi pairing
• Customer walkthrough of the basic ride start process

After completing the course material, you must pass the certification quiz with a score of 80% or higher.`,
    passingScore: 80,
    _count: { quizQuestions: 5 },
    quizQuestions: [], // populated from EQUIPMENT_QUIZ_QUESTIONS in QuizScreen
  },
  {
    id: 'course-peloton-bike-plus',
    title: 'Peloton Bike+',
    categorySlug: 'peloton-bike-plus',
    defaultEquipment: 'peloton-bike-plus',
    _sortOrder: 3,
    status: 'PUBLISHED',
    videoUrl: 'https://youtube.com/watch?v=mx83vZZxIyo',
    textContent: `Peloton Bike+ — Pickup & Delivery Guide

WATCH THE DISASSEMBLY VIDEO BEFORE YOU START — the Bike+ is slightly different from the regular Bike.

TOOLS NEEDED:
• Small allen key to remove the safety key. Ask the seller if they have a Peloton toolkit.

PRE-PICKUP INSPECTION:
• Ask the seller about stairs and arrange help if needed. The flywheel side of the bike should face the bottom of the stairs when carrying.
• Test the screen: Tap the WiFi icon in the top right corner → try connecting to WiFi so the keyboard pops up → make sure the keyboard and touch screen work properly.
• Test ride the bike: If logged out, go to "Get Started" → choose language → select "Sample Class" or "Just Ride". Verify: (1) Resistance adjusts smoothly with the knob and shows on screen, (2) Cadence metric works when you pedal, (3) Sound works properly.
• Sit on the bike, pedal, and adjust resistance. Make sure pedals and flywheel are not making any unusual noises.
• Ask the seller how many rides are on the bike and if there are any known issues.
• If the bike is dusty or sweaty, ask the seller for a wet paper towel and wipe it down. Buyers complain about cleanliness.

DISASSEMBLY STEPS:
1. Remove the safety screw from under the handlebar stem — use a small allen key
2. Unplug the cables from under the handlebars — CAREFUL with the USB-C cable, DO NOT break the tip by pulling on the handlebars before unplugging
3. Remove the handlebars with the screen from the bike frame

DELIVERY & SETUP:
• Safe transport of the 140 lb unit (requires 2 people)
• 23.8" rotating HD touchscreen — handle with extreme care
• Screen rotates 180° for off-bike workouts — verify rotation clearance
• Auto-follow resistance feature — pair with customer's account during setup
• Power cable connection and WiFi pairing
• Customer walkthrough of the basic ride start process

After completing the course material, you must pass the certification quiz with a score of 80% or higher.`,
    passingScore: 80,
    _count: { quizQuestions: 5 },
    quizQuestions: [],
  },
  {
    id: 'course-peloton-tread',
    title: 'Peloton Tread',
    categorySlug: 'peloton-tread',
    defaultEquipment: 'peloton-tread',
    _sortOrder: 4,
    status: 'PUBLISHED',
    videoUrl: 'https://youtube.com/watch?v=dGGJc6XchZU',
    textContent: `Peloton Tread — Pickup & Delivery Guide

WATCH THE DISASSEMBLY VIDEO BEFORE YOU START — the Tread has many steps and is very heavy.

TOOLS NEEDED:
• 8mm Allen Key for the big bolts, small allen key for the 2 corner covers.

PRE-PICKUP INSPECTION:
• Ask the seller about stairs and make sure you have at least one person to help. The tread is very heavy.
• Test the screen: Tap the WiFi icon in the top right corner → connect to WiFi → verify touch and keyboard work.
• Test run: If logged out, go to "Get Started" → choose language → select "Sample Class" or "Just Run". Verify: (1) Elevation adjusts smoothly and the number shows on screen, (2) Speed controls work without weird noises, (3) Sound works properly.
• Walk on the tread and test the elevation/incline. Make sure it works smoothly and is not making any unusual noises.
• Ask the seller how many runs are on the tread and if there are any known issues.
• If the tread is dusty or sweaty, ask the seller for a wet paper towel and wipe it down.

DISASSEMBLY STEPS:
1. Set incline to "3" — this aligns the wheels so it rolls without breaking the switch
2. Remove the caps in the middle of each arm to uncover the bolts
3. Loosen the bolts on both arms
4. Have someone hold the console while you disconnect the 3 cables from the left arm
5. Remove the top console (no need to remove the screen from it)
6. Remove cover on bottom left arm corner, take out the 2 small bolts
7. Disconnect all 3 wires on the left side (no wiring on right side)
8. Remove the 5 large bolts attaching the left arm to the frame
9. Remove the right arm (5 bolts, no wires)
10. Tape the switch at the bottom of the tread

MOVING:
• Make sure the switch and reset button on the end don't snag on the floor — don't angle the end too low.
• For stairs: use a hand truck, switch/reset button at the top.
• If carrying: need at least 2 people, grab the base by the arm openings on front and the handle grip on the back.

After completing the course material, you must pass the certification quiz with a score of 80% or higher.`,
    passingScore: 80,
    _count: { quizQuestions: 5 },
    quizQuestions: [],
  },
  {
    id: 'course-peloton-tread-plus',
    title: 'Peloton Tread+',
    categorySlug: 'peloton-tread-plus',
    defaultEquipment: 'peloton-tread-plus',
    _sortOrder: 5,
    status: 'PUBLISHED',
    videoUrl: 'https://youtube.com/watch?v=GONcOJ55P0Q',
    textContent: `Peloton Tread+ — Pickup & Delivery Guide

WATCH THE DISASSEMBLY VIDEO BEFORE YOU START.

PRE-PICKUP INSPECTION:
• The Tread+ base is EXTREMELY heavy. You need at least 2 additional people for stairs.
• Test the screen: Tap WiFi icon → connect → verify touch and keyboard work.
• Test run: "Get Started" → language → "Sample Class" or "Just Run". Verify elevation, speed, and sound.
• Walk on the tread and test the elevation. Make sure it works smoothly without unusual noises.
• Ask the seller how many runs and any known issues.
• If dusty or sweaty, ask the seller for a wet paper towel and wipe it down.

DISASSEMBLY STEPS:
• Keep the screen attached to the console and arms for transit. Only disconnect the arms from the base.
1. Remove the grey cover under the left arm
2. Remove the end cap to reach 2 of the 6 bolts — check that grey clips don't stay in the machine (use pliers if needed)
3. On the right side, disconnect 2 cables: white cable and black cable
4. Remove all bolts from both sides, lift the arms+console+screen as one piece
5. Lay it down level — don't put too much weight on either side

MOVING THE BASE:
• The base is extremely heavy — DO NOT drag it. Use a hand truck or dolly.
• Front of the tread should be at the bottom of the hand truck.
• Dedicated 20A circuit requirement — verify before delivery.
• Minimum ceiling clearance: user height + 20 inches.

After completing the course material, you must pass the certification quiz with a score of 80% or higher.`,
    passingScore: 80,
    _count: { quizQuestions: 5 },
    quizQuestions: [],
  },
  {
    id: 'course-peloton-row',
    title: 'Peloton Row',
    categorySlug: 'peloton-row',
    defaultEquipment: 'peloton-row',
    _sortOrder: 6,
    status: 'PUBLISHED',
    videoUrl: null,
    textContent: `Peloton Row — Pickup & Delivery Guide

PRE-PICKUP INSPECTION:
• Ask about stairs and arrange help if needed. Monitor side of the row should face the bottom of the stairs when carrying.
• Test the screen: Tap WiFi icon → connect → verify touch and keyboard work.
• Test row: If logged out, "Get Started" → language → "Sample Class". Verify: (1) Resistance adjusts smoothly with the knob and shows on screen, (2) Cadence metric works when you row, (3) Sound works properly.
• Sit on the row and make sure it works properly. Check that the cable is not ripped or making any unusual noise when in use.
• Ask the seller how many sessions are on the Row and if there are any known issues.
• If dusty or sweaty, ask the seller for a wet paper towel and wipe it down. Buyers complain about cleanliness.

DELIVERY & SETUP:
• Safe transport of the 156 lb unit
• 23.8" HD touchscreen — same fragility precautions as Bike+
• 8' × 4' minimum floor space requirement
• Form Assist camera positioning — no mirrors behind
• Leveling on all four contact points
• Strap tension adjustment and customer technique walkthrough

After completing the course material, you must pass the certification quiz with a score of 80% or higher.`,
    passingScore: 80,
    _count: { quizQuestions: 5 },
    quizQuestions: [],
  },
  {
    id: 'course-sauna',
    title: 'Sauna Delivery & Assembly',
    categorySlug: 'sauna',
    _sortOrder: 8,
    status: 'PUBLISHED',
    videoUrl: 'https://youtube.com/watch?v=9s7653HjskU',
    textContent: `Sauna — Pickup & Delivery Guide

PRE-PICKUP INSPECTION:
• Identify sauna type: infrared panel sauna, traditional barrel sauna, or steam sauna. Measure dimensions and confirm it fits in your vehicle.
• If still connected to power: (1) Turn on and verify heater/infrared panels warm up, (2) Check control panel, (3) Test interior lights, (4) Check door seal and hinges.
• Inspect wood and interior: Check for (1) Wood rot, warping, or discoloration, (2) Mold or mildew (common in saunas), (3) Bench condition — sturdy, no cracks, (4) Glass door — no cracks or chips, (5) Exterior wood condition.
• Ask about age, how often used, any heating issues, wood treatments applied, and if they have assembly instructions for reassembly.

DISASSEMBLY & MOVING:
• Many saunas can be disassembled into panels: (1) Remove benches and interior accessories first, (2) If panel-style: unscrew wall panels and roof (label each panel), (3) Wrap panels individually with moving blankets, (4) If barrel-style: will need to move as one piece — very heavy, need 2+ people, (5) Protect all glass components separately.

DELIVERY & ASSEMBLY:
• Pre-delivery site inspection — verify level ground, clear access path, and minimum 5-foot clearance from structures
• Barrel sauna assembly — install bottom cradle first, then staves in sequence, tighten steel bands evenly
• Heater placement — mount near the door wall with required ventilation clearance (per manufacturer spec)
• Electrical requirements — dedicated circuit matching heater wattage; most residential saunas need 240V / 30A–60A
• Customer walkthrough — cover temperature controls, ventilation vents, cool-down procedure, and emergency procedures

After completing the course material, you must pass the certification quiz with a score of 80% or higher.`,
    passingScore: 80,
    _count: { quizQuestions: 5 },
    quizQuestions: [
      { id: 'sauna-q1', questionText: 'What should be checked at the delivery site before unloading?', options: ['Level ground and clear path', 'Electrical outlet only', 'Nothing — just deliver', 'Customer signature'], correctIndex: 0 },
      { id: 'sauna-q2', questionText: 'How far should the sauna be from the house or other structures?', options: ['Directly against the wall', 'At least 3 feet', 'At least 5 feet', 'At least 10 feet'], correctIndex: 2 },
      { id: 'sauna-q3', questionText: 'What is the correct assembly order for barrel sauna staves?', options: ['Start from the top', 'Start from the bottom cradle', 'Random order', 'All at once'], correctIndex: 1 },
      { id: 'sauna-q4', questionText: 'Where should the heater be placed inside the sauna?', options: ['In the center of the floor', 'Near the door wall with proper ventilation clearance', 'Anywhere convenient', 'Outside the sauna enclosure'], correctIndex: 1 },
      { id: 'sauna-q5', questionText: 'What must be verified about the electrical supply before powering on?', options: ['Any standard 120V outlet works', 'Dedicated circuit matching heater specs (typically 240V / 30A–60A)', 'An extension cord rated for outdoor use is fine', 'Battery backup is required'], correctIndex: 1 },
    ],
  },
  {
    id: 'course-tonal',
    title: 'Tonal Smart Gym Installation',
    categorySlug: 'tonal',
    _sortOrder: 7,
    status: 'PUBLISHED',
    videoUrl: null,
    textContent: `Tonal Smart Gym — Pickup & Delivery Guide

IMPORTANT: Always need exactly 2 people — no more, no less.

PRE-PICKUP INSPECTION:
• Confirm stairs & wall mount situation. Tonal is mounted to the wall with heavy-duty brackets — you will need to remove it. Confirm if the wall is drywall, concrete, or brick.
• Test power: Power on and verify (1) Screen turns on and is responsive to touch, (2) No dead pixels, cracks, or screen burn-in, (3) Sound works from built-in speakers.
• Test both arms: Pull both arms out fully and test (1) Arms extend and retract smoothly, (2) Cables are not frayed, kinked, or showing wear, (3) Resistance adjusts when prompted on screen, (4) Both arms feel equal in resistance.
• Collect ALL accessories and verify: handles, rope, bar, roller, bench (if included), mat, and any other accessories listed. Check the hooks on the side of the Tonal for stored attachments.
• Ask the seller how often they used it, any known issues with the arms/cables/screen, and if they have the original packaging or wall mount hardware.
• If dusty or has sweat residue, ask for a cloth and wipe down the screen, arms, and body. Buyers expect a clean product.

WALL REMOVAL STEPS:
1. Power off and unplug the Tonal
2. Remove the arms and accessories first
3. Detach the main unit from the wall mount bracket (it's heavy — have someone help)
4. Remove the wall mount bracket from the wall
5. Collect all wall mount hardware and bolts in a bag
6. Wrap the screen face with a moving blanket to prevent scratches

DELIVERY & INSTALLATION:
• Minimum 7 feet ceiling height, 7 feet floor space in front, 2 feet clearance on each side
• Dedicated 15A / 120V outlet within 3 feet — no extension cords or power strips
• Use a stud finder — mount bracket must anchor into two solid wood studs at 16" on center
• Run the built-in weight calibration sequence to verify all 200 levels of digital resistance
• Walk through account pairing, arm selection, cable attachment points, and a sample exercise

After completing the course material, you must pass the certification quiz with a score of 80% or higher.`,
    passingScore: 80,
    _count: { quizQuestions: 5 },
    quizQuestions: [
      { id: 'tonal-q1', questionText: 'What is the approximate weight of a Tonal unit?', options: ['75 lbs', '100 lbs', '150 lbs', '200 lbs'], correctIndex: 2 },
      { id: 'tonal-q2', questionText: 'What must the mounting bracket be anchored into?', options: ['Drywall with toggle bolts', 'Two solid wood studs at 16" on center', 'Concrete backer board', 'Any wall surface with adhesive'], correctIndex: 1 },
      { id: 'tonal-q3', questionText: 'What are the electrical requirements for a Tonal installation?', options: ['Any available outlet in the room', 'Dedicated 15A / 120V outlet within 3 feet — no extension cords', 'Dedicated 20A / 240V outlet', 'Hardwired connection by an electrician'], correctIndex: 1 },
      { id: 'tonal-q4', questionText: 'What is the minimum ceiling height required for Tonal?', options: ['6 feet', '7 feet', '8 feet', '9 feet'], correctIndex: 1 },
      { id: 'tonal-q5', questionText: 'What must be run after powering on the Tonal for the first time?', options: ['A firmware update only', 'The built-in weight calibration sequence', 'A 30-minute burn-in test', 'Nothing — it is ready to use immediately'], correctIndex: 1 },
    ],
  },
  {
    id: 'course-hot-tub',
    title: 'Hot Tub Delivery & Setup',
    categorySlug: 'hot-tub',
    _sortOrder: 9,
    status: 'PUBLISHED',
    videoUrl: null,
    textContent: `Hot Tub — Pickup & Delivery Guide

IMPORTANT: Hot tubs are extremely heavy (500-800 lbs empty). Minimum 4 people needed for most hot tubs.

PRE-PICKUP INSPECTION:
• Confirm access path: Measure gates, doorways, tight corners. Determine whether a crane, dolly, or furniture sliders are needed. Check if it needs to go over a fence or wall.
• The hot tub MUST be completely drained before pickup. A full hot tub can weigh 3,000-6,000 lbs. Verify there is no standing water in the tub, jets, or plumbing lines.
• Inspect the shell: Check acrylic for cracks, chips, stress fractures, discoloration, staining. Check that all jets are present and intact. Check headrests and pillows.
• Check the cover: Is it included? (Covers cost $300-$800 to replace.) Lift it — should not be extremely heavy (waterlogging). Check vinyl for tears, fading, or mold.
• If still connected to power, ask the seller to demo: pump turns on, jets work, heater functions, control panel responds, lights work.
• Ask about age, any leaks or repairs, when last serviced, pump/heater replacements, any known issues.

MOVING:
1. Tip on its side (2-side on bottom) for transport
2. Use furniture dollies or PVC pipe rollers to slide across flat surfaces
3. Protect the shell with moving blankets
4. Secure upright in the truck — do not stack items on top
5. Bring plenty of ratchet straps

DELIVERY & SETUP:
• Site must be level, reinforced pad (concrete slab, reinforced deck, or compacted gravel pad rated for 3,000–5,000 lbs filled weight)
• Electrical: dedicated 240V / 50A GFCI-protected circuit by a licensed electrician; disconnect panel within line of sight
• Fill with garden hose, balance pH (7.2–7.8), alkalinity (80–120 ppm), and sanitizer
• Run all jets and pumps, inspect every connection for drips during first 30 minutes
• Customer walkthrough — control panel, cover lifter, chemical dosing schedule, winterization

After completing the course material, you must pass the certification quiz with a score of 80% or higher.`,
    passingScore: 80,
    _count: { quizQuestions: 5 },
    quizQuestions: [
      { id: 'hottub-q1', questionText: 'What is the typical weight range of an empty hot tub?', options: ['100–200 lbs', '300–400 lbs', '500–800 lbs', '1,000–1,500 lbs'], correctIndex: 2 },
      { id: 'hottub-q2', questionText: 'What type of electrical circuit do most full-size hot tubs require?', options: ['Standard 120V / 15A outlet', 'Dedicated 240V / 50A GFCI-protected circuit', 'Dedicated 120V / 30A circuit', 'Any outdoor outlet with a weather cover'], correctIndex: 1 },
      { id: 'hottub-q3', questionText: 'What is the acceptable pH range for hot tub water?', options: ['6.0–6.5', '7.2–7.8', '8.5–9.0', '5.5–6.0'], correctIndex: 1 },
      { id: 'hottub-q4', questionText: 'What must the hot tub placement surface be rated to support?', options: ['The empty weight of the tub only', 'At least 1,000 lbs', 'The filled weight of 3,000–5,000 lbs', 'It can sit on any flat surface'], correctIndex: 2 },
      { id: 'hottub-q5', questionText: 'What should you inspect during the first 30 minutes of operation?', options: ['Only the water temperature', 'Every union and jet connection for leaks', 'The cover seal only', 'Nothing — let the customer handle it'], correctIndex: 1 },
    ],
  },
  {
    id: 'course-treadmill',
    title: 'General Treadmill Delivery & Setup',
    categorySlug: 'treadmill',
    _sortOrder: 20,
    status: 'PUBLISHED',
    videoUrl: null,
    textContent: `General Treadmill — Pickup & Delivery Guide

PRE-PICKUP INSPECTION:
• Test: (1) Belt moves smoothly at various speeds, (2) Incline mechanism works up and down, (3) Console/display powers on and is responsive, (4) Emergency stop/safety key works, (5) No unusual motor noise or belt slipping.
• Inspect: (1) Belt — fraying edges, thinning, or off-center tracking, (2) Deck — lift belt and check for cracks or warping, (3) Frame — rust, cracks, loose bolts, (4) Side rails — secure, not wobbly, (5) Console — all buttons work.

MOVING:
• If it folds: (1) Engage folding mechanism and lock upright, (2) Secure with transport lock or strap, (3) Most have transport wheels — tilt back and roll.
• If it doesn't fold: carry flat, 200-350 lbs, need 2 people minimum.
• Wrap the console/display with a blanket. Secure the power cord so it doesn't drag. Strap upright in the truck.

DELIVERY & SETUP:
• Measure all entry points before delivery; remove doors from hinges if needed; tilt to navigate tight turns
• Belt tension and alignment — belt should deflect 2–3 inches when lifted from center; adjust rear roller bolts in quarter-turn increments
• Plug into a dedicated 15A or 20A circuit — never use an extension cord; surge protector recommended
• Attach safety key lanyard, test emergency stop, walk customer through speed/incline controls

After completing the course material, you must pass the certification quiz with a score of 80% or higher.`,
    passingScore: 80,
    _count: { quizQuestions: 5 },
    quizQuestions: [
      { id: 'tmill-q1', questionText: 'What is the typical weight range of a residential treadmill?', options: ['50–100 lbs', '100–200 lbs', '200–400 lbs', '500–700 lbs'], correctIndex: 2 },
      { id: 'tmill-q2', questionText: 'How much should the treadmill belt deflect when lifted from the center?', options: ['Less than 1 inch', '2–3 inches', '5–6 inches', 'The belt should not move at all'], correctIndex: 1 },
      { id: 'tmill-q3', questionText: 'What should you use to adjust belt alignment?', options: ['A hammer on the front roller', 'Rear roller bolts in quarter-turn increments', 'Loosening the motor mount', 'Belt alignment is factory-set and cannot be adjusted'], correctIndex: 1 },
      { id: 'tmill-q4', questionText: 'What type of circuit is recommended for a treadmill?', options: ['Any household outlet', 'A dedicated 15A or 20A circuit with no extension cord', 'A 240V dryer outlet', 'A power strip with surge protection is sufficient'], correctIndex: 1 },
      { id: 'tmill-q5', questionText: 'What safety device must be tested before leaving the customer\'s home?', options: ['The heart-rate monitor', 'The safety key and emergency stop', 'The console brightness sensor', 'The WiFi connection'], correctIndex: 1 },
    ],
  },
  {
    id: 'course-rowing-machine',
    title: 'General Rowing Machine Delivery & Setup',
    categorySlug: 'rowing-machine',
    _sortOrder: 21,
    status: 'PUBLISHED',
    videoUrl: null,
    textContent: `Complete guide to rowing machine delivery, assembly, and customer setup for non-Peloton rower brands.

Key topics:
• Weight and dimensions — rowing machines typically weigh 80–150 lbs; most come partially disassembled and require single-person or two-person carry
• Floor space requirements — minimum 9 feet of length and 4 feet of width; add 2 feet behind the rail for full seat travel and clearance
• Rail and seat assembly — attach the monorail to the frame base, install the seat roller carriage, and verify smooth glide with no binding or clicking
• Damper and resistance settings — set the damper lever to the mid-range position (3–5 on most air rowers) for the customer demo; explain the difference between damper setting and actual resistance
• Floor protection — place the rower on a rubber equipment mat to prevent scratches and reduce vibration; verify the unit does not wobble on the mat
• Customer demo — show proper foot strap adjustment, handle grip, and the catch-drive-finish-recovery stroke sequence on the monitor`,
    passingScore: 80,
    _count: { quizQuestions: 5 },
    quizQuestions: [
      { id: 'rower-q1', questionText: 'What is the typical weight range of a rowing machine?', options: ['30–50 lbs', '80–150 lbs', '200–300 lbs', '400–500 lbs'], correctIndex: 1 },
      { id: 'rower-q2', questionText: 'What is the minimum recommended floor length for a rowing machine?', options: ['6 feet', '7 feet', '9 feet', '12 feet'], correctIndex: 2 },
      { id: 'rower-q3', questionText: 'What should you check after installing the seat roller carriage on the rail?', options: ['That the monitor powers on', 'Smooth glide with no binding or clicking', 'That the damper is set to 10', 'The foot straps are pre-tightened'], correctIndex: 1 },
      { id: 'rower-q4', questionText: 'What damper setting should be used for the customer demonstration on an air rower?', options: ['1 (minimum)', 'Mid-range (3–5)', '10 (maximum)', 'Damper setting does not matter'], correctIndex: 1 },
      { id: 'rower-q5', questionText: 'What should be placed under the rowing machine to protect the floor?', options: ['A towel', 'A rubber equipment mat', 'Cardboard from the packaging', 'Nothing — rowers are floor-safe'], correctIndex: 1 },
    ],
  },
  // NOTE: course-general removed — General Delivery Basics is covered by driver-certification.
  // After completing driver-cert, drivers only need category-specific quizzes.
  {
    id: 'course-appliance',
    title: 'Large Appliance Delivery & Installation',
    categorySlug: 'appliance',
    _sortOrder: 22,
    status: 'PUBLISHED',
    videoUrl: null,
    textContent: `Large Appliance — Pickup & Delivery Guide

PRE-PICKUP INSPECTION:
• Ask the seller to demonstrate: (1) Powers on and completes a cycle or reaches temperature, (2) No error codes, (3) No unusual noises, (4) All doors, drawers, and handles work properly.
• Inspect: (1) Dents, scratches, or rust on exterior, (2) Interior — clean, no cracks or broken shelves/racks, (3) Door seals/gaskets — no tears or mold, (4) All knobs, buttons, displays work, (5) Power cord not frayed.

BEFORE MOVING:
1. Unplug from power
2. Refrigerators: must be unplugged 4+ hours and defrosted, secure doors with tape
3. Washers: disconnect water supply, drain remaining water
4. Gas appliances: confirm gas is OFF and line disconnected (seller should do this)
5. Secure all loose parts

MOVING:
• Use an appliance dolly — never drag
• Secure doors and drawers with tape or straps
• Refrigerators: keep upright during transport (if tipped, must wait 24h before plugging in)
• Protect glass cooktops with cardboard and blankets

DELIVERY & SETUP:
• Measure all entry points; remove doors from hinges if necessary
• Use cardboard or moving blankets to protect floors
• Verify outlet type and voltage before connecting; do not modify existing wiring
• Use adjustable feet and a bubble level to ensure appliance is stable and doors seal properly
• Water line connections: hand-tighten supply lines, check for leaks before leaving
• Customer walkthrough — demonstrate basic operation, filter locations, and maintenance tips

After completing the course material, you must pass the certification quiz with a score of 80% or higher.`,
    passingScore: 80,
    _count: { quizQuestions: 5 },
    quizQuestions: [
      { id: 'app-q1', questionText: 'What is the typical weight range of large household appliances?', options: ['50–100 lbs', '150–500+ lbs', '10–50 lbs', 'Under 50 lbs always'], correctIndex: 1 },
      { id: 'app-q2', questionText: 'What should you do before connecting an appliance to a power outlet?', options: ['Plug it in and see what happens', 'Verify the outlet type and voltage match the appliance requirements', 'Use an extension cord if needed', 'Let the customer handle all electrical connections'], correctIndex: 1 },
      { id: 'app-q3', questionText: 'How should you protect floors when moving heavy appliances?', options: ['Drag the appliance slowly', 'Use cardboard or moving blankets under the path', 'Only worry about carpet', 'Floors can handle it — no protection needed'], correctIndex: 1 },
      { id: 'app-q4', questionText: 'After connecting a water line to a refrigerator or washer, what must you check?', options: ['That the water is cold', 'For leaks at every connection point', 'That the ice maker works immediately', 'Nothing — hand-tight is sufficient'], correctIndex: 1 },
      { id: 'app-q5', questionText: 'Why is it important to level an appliance after placement?', options: ['It looks better', 'Ensures doors seal properly and the appliance operates correctly', 'It prevents electrical issues', 'Leveling is optional for most appliances'], correctIndex: 1 },
    ],
  },
  {
    id: 'course-cold-plunge',
    title: 'Cold Plunge & Recovery Equipment Delivery',
    categorySlug: 'cold-plunge',
    _sortOrder: 23,
    status: 'PUBLISHED',
    videoUrl: null,
    textContent: `Cold Plunge — Pickup & Delivery Guide

PRE-PICKUP INSPECTION:
• The cold plunge must be completely drained and dry before pickup. Check for any standing water in the tub or filtration system.
• If still connected to power: (1) Verify the chiller/cooling unit powers on, (2) Check the control panel and temperature display, (3) Test the filtration pump, (4) Check for any error codes.
• Inspect: (1) Interior tub surface — cracks, stains, discoloration, (2) Exterior condition, (3) Lid/cover present and in good shape, (4) All hoses and fittings intact, (5) Chiller unit condition (usually a separate box).
• Ask about age, any leaks, chiller performance, filter replacement history, and if they have the original manual.

MOVING:
1. Disconnect chiller unit from tub (keep hoses organized)
2. Wrap the tub with moving blankets
3. Transport the chiller unit separately — protect the compressor
4. Keep all hoses, fittings, and accessories in a labeled bag

DELIVERY & SETUP:
• Placement area must support filled weight (500–1,500 lbs with water), level surface near power outlet and water source
• Dedicated 15A or 20A circuit; chiller units draw significant power; no extension cords
• Initial fill, filtration system setup, and basic chemical balance (pH, sanitizer)
• Set target temperature per manufacturer specs (typically 39–50°F)
• Customer walkthrough — temperature controls, filtration maintenance, and safety precautions

After completing the course material, you must pass the certification quiz with a score of 80% or higher.`,
    passingScore: 80,
    _count: { quizQuestions: 5 },
    quizQuestions: [
      { id: 'cp-q1', questionText: 'What should be verified about the placement surface before installing a cold plunge?', options: ['It should be near a window', 'It must be level and support the filled weight (500–1,500 lbs)', 'Any flat surface works', 'It only matters if the plunge is outdoors'], correctIndex: 1 },
      { id: 'cp-q2', questionText: 'What type of electrical circuit do most cold plunge chillers require?', options: ['Any standard outlet', 'Dedicated 15A or 20A circuit — no extension cords', 'A 240V dryer outlet', 'They run on batteries'], correctIndex: 1 },
      { id: 'cp-q3', questionText: 'What is the typical target temperature range for a cold plunge?', options: ['60–70°F', '39–50°F', '32–35°F', '70–80°F'], correctIndex: 1 },
      { id: 'cp-q4', questionText: 'How are most residential cold plunges filled with water?', options: ['They come pre-filled', 'Garden hose fill with optional drain connection', 'Professional plumbing installation required', 'Buckets only'], correctIndex: 1 },
      { id: 'cp-q5', questionText: 'What must be set up after filling a cold plunge with water?', options: ['Nothing — it is ready to use', 'Filtration system, basic chemical balance (pH, sanitizer), and chiller calibration', 'Only the temperature', 'A cover is the only requirement'], correctIndex: 1 },
    ],
  },
  {
    id: 'course-outdoor',
    title: 'Outdoor & Playground Equipment Delivery',
    categorySlug: 'outdoor',
    _sortOrder: 24,
    status: 'PUBLISHED',
    videoUrl: null,
    textContent: `Guide to delivering and assembling outdoor play sets, swing sets, and large outdoor equipment.

Key topics:
• Component inventory — verify all parts, hardware bags, and panels against the packing list before leaving the warehouse
• Weight and transport — large play sets can weigh 500–1,000+ lbs in total; plan for multiple trips or a trailer
• Site assessment — minimum 6 feet of clearance around the structure in all directions; level ground required
• Assembly sequence — follow manufacturer instructions exactly; anchor posts into the ground before adding crossbeams
• Hardware and fastening — use the provided hardware; do not substitute bolts or screws
• Safety inspection — verify all connections are tight, no sharp edges exposed, swings hang level
• Customer walkthrough — cover weight limits, age recommendations, and maintenance schedule`,
    passingScore: 80,
    _count: { quizQuestions: 5 },
    quizQuestions: [
      { id: 'out-q1', questionText: 'What should you verify before leaving the warehouse with outdoor equipment?', options: ['Only the main structure', 'All parts, hardware bags, and panels against the packing list', 'Nothing — the warehouse handles inventory', 'Just the instruction manual'], correctIndex: 1 },
      { id: 'out-q2', questionText: 'What is the minimum clearance required around a play set?', options: ['2 feet', '4 feet', '6 feet in all directions', '10 feet'], correctIndex: 2 },
      { id: 'out-q3', questionText: 'What should be done to the ground before assembling a play set?', options: ['Nothing special', 'Verify it is level and anchor posts into the ground', 'Pour concrete first', 'Only check for rocks'], correctIndex: 1 },
      { id: 'out-q4', questionText: 'Can you substitute hardware if the provided bolts are missing?', options: ['Yes, any similar bolt works', 'No — use only the provided hardware or contact the manufacturer for replacements', 'Yes, as long as it fits', 'Only for non-structural parts'], correctIndex: 1 },
      { id: 'out-q5', questionText: 'What must be inspected after assembly is complete?', options: ['Only the swing chains', 'All connections tight, no sharp edges, swings hang level', 'Just the slide', 'Nothing — assembly is sufficient'], correctIndex: 1 },
    ],
  },
  {
    id: 'course-furniture',
    title: 'Furniture Delivery & Assembly',
    categorySlug: 'furniture',
    _sortOrder: 25,
    status: 'PUBLISHED',
    videoUrl: null,
    textContent: `Furniture — Pickup & Delivery Guide

PRE-PICKUP INSPECTION:
• Before moving: (1) Measure the furniture dimensions, (2) Measure all doorways, hallways, stairwells, and elevators on the path out, (3) Confirm the piece can fit through without disassembly, (4) If it won't fit, determine what can be removed (legs, cushions, shelves).
• Check thoroughly: (1) All surfaces for scratches, stains, water rings, or chips, (2) Structural integrity — wobble test for tables/chairs, (3) Drawers and doors open/close properly, (4) Upholstery for tears, stains, pet damage, or odors, (5) Hardware — all screws, bolts, and hinges intact.

DISASSEMBLY & MOVING:
• If disassembly is needed: (1) Remove legs from tables, sofas, and bed frames, (2) Remove shelves, drawers, and glass components separately, (3) Keep all hardware in a labeled ziplock bag and tape it to the furniture, (4) Take photos of any connections before disassembling.
• Protection: (1) Wrap all surfaces with moving blankets — especially corners and edges, (2) Use cardboard corner protectors, (3) Wrap glass/mirror components separately with bubble wrap, (4) Wrap cushions in plastic wrap, (5) Secure all wrapped pieces in truck so nothing shifts.

DELIVERY & SETUP:
• Use floor runners on hardwood and tile; apply felt pads to furniture legs before placement
• Follow manufacturer instructions exactly; organize hardware by bag number before starting
• Hand-tighten all bolts first, then final-tighten in sequence
• Bed frames: center the mattress, verify slat spacing, attach headboard securely if required
• Confirm placement, demonstrate adjustable features, verify customer satisfaction

After completing the course material, you must pass the certification quiz with a score of 80% or higher.`,
    passingScore: 80,
    _count: { quizQuestions: 5 },
    quizQuestions: [
      { id: 'furn-q1', questionText: 'What should you do before attempting to move a large sofa through a doorway?', options: ['Push it through quickly', 'Measure the doorway and the sofa dimensions first', 'Remove the sofa legs and drag it', 'Only attempt if the customer helps'], correctIndex: 1 },
      { id: 'furn-q2', questionText: 'How should you protect upholstered furniture during transport?', options: ['Leave it uncovered for ventilation', 'Shrink-wrap it to prevent stains and tears', 'Place it on top of other items', 'Only cover it if it is raining'], correctIndex: 1 },
      { id: 'furn-q3', questionText: 'What should be applied to furniture legs before placing on hardwood floors?', options: ['Nothing — just set it down carefully', 'Felt pads to prevent scratches', 'Rubber cement', 'Masking tape'], correctIndex: 1 },
      { id: 'furn-q4', questionText: 'When assembling furniture with multiple hardware bags, what should you do first?', options: ['Start building immediately', 'Organize hardware by bag number before starting', 'Only use the largest bolts', 'Ask the customer to sort the hardware'], correctIndex: 1 },
      { id: 'furn-q5', questionText: 'What is the correct bolt-tightening sequence for furniture assembly?', options: ['Fully tighten each bolt as you go', 'Hand-tighten all bolts first, then final-tighten in sequence', 'Use power tools for speed', 'Tightening order does not matter'], correctIndex: 1 },
    ],
  },
  {
    id: 'course-gym-equipment',
    title: 'Gym Equipment Delivery & Setup',
    categorySlug: 'gym-equipment',
    _sortOrder: 10,
    status: 'PUBLISHED',
    videoUrl: null,
    textContent: `Gym Equipment — Pickup & Delivery Guide

PRE-PICKUP INSPECTION:
• Verify the equipment works: All moving parts (cables, pulleys, rails) operate smoothly, no unusual noises, weight stack moves freely, electronic displays power on, safety features work.
• Inspect: (1) Frame — rust, cracks, bent sections, loose welds, (2) Cables — fraying, kinks, or wear spots (safety critical!), (3) Upholstery/pads — tears, cracks, excessive wear, (4) Pulleys and bearings — spin freely, no wobble, (5) Weight plates/stack — complete set, no cracks.

DISASSEMBLY & MOVING:
• Most gym equipment needs partial disassembly: (1) Remove cables/attachments and label them, (2) Remove weight plates and stack separately, (3) Detach uprights from base if possible, (4) Keep all pins, bolts, and hardware in a labeled bag, (5) Take photos of cable routing before disconnecting, (6) Some machines are 300-500+ lbs — confirm you have enough help.
• Moving: (1) Use a furniture dolly for heavy bases, (2) Protect chrome/metal parts with blankets — scratches easily, (3) Keep weight plates flat in the truck, not stacked vertically, (4) Secure all loose parts, (5) For squat racks/power racks: may need to completely disassemble.

DELIVERY & SETUP:
• Place rubber gym mats or plywood under all heavy equipment
• Organize all hardware first; follow manual step-by-step
• Cable routing — thread cables through pulleys exactly as diagrammed; incorrect routing causes binding or cable failure
• Bolt squat racks and tall systems to the floor or wall per manufacturer specs to prevent tipping
• Verify weight stack moves smoothly through full range; pin engages securely at each increment
• Walk through all exercise stations, cable attachment points, safety catches, and weight adjustments

After completing the course material, you must pass the certification quiz with a score of 80% or higher.`,
    passingScore: 80,
    _count: { quizQuestions: 5 },
    quizQuestions: [
      { id: 'gym-q1', questionText: 'Why is it critical to check all boxes against the packing list before leaving the warehouse?', options: ['To save time on the drive', 'Missing parts can significantly delay installation at the customer site', 'The warehouse requires a signature', 'It is optional if the customer is flexible'], correctIndex: 1 },
      { id: 'gym-q2', questionText: 'What should be placed under heavy gym equipment to protect floors?', options: ['Cardboard from the packaging', 'Rubber gym mats or plywood for proper weight distribution', 'A thin towel', 'Nothing — gym equipment has rubber feet'], correctIndex: 1 },
      { id: 'gym-q3', questionText: 'What happens if cables are routed incorrectly through pulleys on a multi-gym?', options: ['Nothing noticeable', 'Binding, uneven resistance, or cable failure', 'The machine still works fine', 'Only cosmetic issues'], correctIndex: 1 },
      { id: 'gym-q4', questionText: 'Why must squat racks and tall gym systems be anchored?', options: ['To look more professional', 'To prevent tipping during use — per manufacturer safety specs', 'Anchoring is optional for home use', 'Only commercial gyms require anchoring'], correctIndex: 1 },
      { id: 'gym-q5', questionText: 'What must be verified after assembling a cable machine weight stack?', options: ['Only that the top weight lifts', 'The stack moves smoothly through full range and the pin engages securely at each increment', 'That the cables are tight', 'Nothing — the manufacturer pre-tests everything'], correctIndex: 1 },
    ],
  },
  {
    id: 'course-electronics',
    _sortOrder: 26,
    title: 'Electronics & TV Delivery',
    categorySlug: 'electronics',
    status: 'PUBLISHED',
    videoUrl: null,
    textContent: `Complete guide to delivering large electronics including TVs, desktop computers, and home entertainment systems.

Key topics:
• Fragility awareness — electronics are highly sensitive to impact, vibration, and temperature extremes; always handle with clean, dry hands or cotton gloves
• TV transport — always transport TVs upright (never flat); secure with straps to prevent tipping; use original packaging whenever possible
• Screen protection — never press on LCD/OLED screens; use foam corner protectors and screen-side padding; label "THIS SIDE UP" and "FRAGILE"
• Temperature acclimation — if transported in cold weather, let electronics sit at room temperature for 1–2 hours before powering on to prevent condensation damage
• Unboxing procedure — cut packaging tape carefully (never stab into boxes); remove foam inserts gently; keep packaging until customer confirms the item works
• Wall mounting (TVs) — use a stud finder, mount into solid wood studs, verify the mount is rated for the TV weight; route cables cleanly
• Customer setup — power on, verify no dead pixels or shipping damage on screens, connect to WiFi if applicable, and confirm customer satisfaction`,
    passingScore: 80,
    _count: { quizQuestions: 5 },
    quizQuestions: [
      { id: 'elec-q1', questionText: 'How should a large TV be transported in a vehicle?', options: ['Flat on its back for stability', 'Upright, secured with straps to prevent tipping', 'Screen-side down for protection', 'Any orientation is fine in the original box'], correctIndex: 1 },
      { id: 'elec-q2', questionText: 'Why should you wait before powering on electronics delivered in cold weather?', options: ['To let the battery charge', 'To prevent condensation damage — let them reach room temperature first (1–2 hours)', 'Cold weather has no effect on electronics', 'To check for shipping damage first'], correctIndex: 1 },
      { id: 'elec-q3', questionText: 'What should you never do when unboxing electronics?', options: ['Cut tape carefully', 'Keep the packaging', 'Stab into boxes with a blade — you may damage the product', 'Remove foam inserts'], correctIndex: 2 },
      { id: 'elec-q4', questionText: 'When wall-mounting a TV, what must the mount be secured into?', options: ['Drywall with toggle bolts is sufficient', 'Solid wood studs rated for the TV weight', 'Any wall surface with adhesive strips', 'The mount bracket does not need wall attachment'], correctIndex: 1 },
      { id: 'elec-q5', questionText: 'What should be checked after powering on a delivered TV?', options: ['Only that it turns on', 'No dead pixels, no shipping damage on the screen, and WiFi connectivity', 'Just the remote control', 'Nothing — the customer will check it themselves'], correctIndex: 1 },
    ],
  },
  {
    id: 'course-golf-simulator',
    _sortOrder: 27,
    title: 'Golf Simulator Delivery & Setup',
    categorySlug: 'golf-simulator',
    status: 'PUBLISHED',
    videoUrl: null,
    textContent: `Complete guide to delivering and setting up residential golf simulators including enclosures, projectors, launch monitors, and hitting mats.

Key topics:
• Space requirements — minimum 10 feet wide, 15 feet deep, and 9–10 feet ceiling height; verify dimensions before delivery
• Component inventory — simulators include the enclosure frame, impact screen, projector, launch monitor, hitting mat, and software system; verify all components
• Enclosure assembly — build the frame first, ensure it is level and square, then attach the impact screen with proper tension (no wrinkles or sag)
• Projector mounting — mount at the manufacturer-specified throw distance; align the image to fill the screen evenly; calibrate focus and keystone
• Launch monitor placement — position precisely per manufacturer specs (overhead vs. floor-mounted); calibrate ball detection zone
• Hitting mat placement — center the mat with the screen; verify the tee position aligns with the launch monitor sensor zone
• Cable management — route all cables safely along walls or in conduit to prevent trip hazards; use surge protectors for all electronics
• Customer demo — walk through software setup, game modes, and calibration adjustments`,
    passingScore: 80,
    _count: { quizQuestions: 5 },
    quizQuestions: [
      { id: 'golf-q1', questionText: 'What is the minimum ceiling height typically required for a golf simulator?', options: ['7 feet', '8 feet', '9–10 feet', '12 feet'], correctIndex: 2 },
      { id: 'golf-q2', questionText: 'What must be verified about the impact screen after attaching it to the frame?', options: ['It should be loosely draped', 'Proper tension with no wrinkles or sag', 'It should touch the floor', 'Only the center needs to be taut'], correctIndex: 1 },
      { id: 'golf-q3', questionText: 'Where should the projector be mounted?', options: ['Anywhere with a clear view', 'At the manufacturer-specified throw distance with proper alignment', 'As close to the screen as possible', 'Behind the hitting mat'], correctIndex: 1 },
      { id: 'golf-q4', questionText: 'Why is cable management important in a golf simulator setup?', options: ['For aesthetics only', 'To prevent trip hazards and protect electronics with surge protectors', 'Cables do not need management', 'Only for commercial installations'], correctIndex: 1 },
      { id: 'golf-q5', questionText: 'What must be aligned with the launch monitor sensor zone?', options: ['The projector', 'The tee position on the hitting mat', 'The impact screen edges', 'The room lighting'], correctIndex: 1 },
    ],
  },
  {
    id: 'course-pool-table',
    _sortOrder: 28,
    title: 'Pool Table & Game Room Delivery',
    categorySlug: 'pool-table',
    status: 'PUBLISHED',
    videoUrl: null,
    textContent: `Complete guide to delivering, assembling, and leveling pool tables and game room equipment.

Key topics:
• Weight and components — pool tables weigh 500–1,000+ lbs fully assembled; they arrive disassembled with slate, frame, rails, and felt
• Slate handling — slate pieces weigh 150–250 lbs each; always carry on edge with two people minimum; never drag slate across floors
• Frame assembly — build the frame and legs first, level the frame precisely using shims; the frame must be perfectly level before placing slate
• Slate installation — place slate sections on the frame, align seams, and fill joints with beeswax; the playing surface must be dead-level (use a machinist level)
• Felt installation — stretch the felt evenly across the slate with no wrinkles or bubbles; staple underneath the rails; trim excess cleanly
• Rail attachment — bolt the rails in sequence, verifying cushion bounce consistency at each rail
• Leveling — final leveling is critical; use a precision level and adjust leg levelers until a ball placed at center does not drift in any direction
• Customer walkthrough — demonstrate proper felt care, chalk use, and cover placement for protection`,
    passingScore: 80,
    _count: { quizQuestions: 5 },
    quizQuestions: [
      { id: 'pool-q1', questionText: 'How should slate pieces be carried?', options: ['Flat with one person', 'On edge with two people minimum — never drag across floors', 'Rolled across the floor', 'Only with a forklift'], correctIndex: 1 },
      { id: 'pool-q2', questionText: 'What must be done before placing slate sections on the frame?', options: ['Apply felt to the slate first', 'Level the frame precisely using shims', 'Nothing — slate self-levels', 'Attach the rails first'], correctIndex: 1 },
      { id: 'pool-q3', questionText: 'What is used to fill the joints between slate sections?', options: ['Wood glue', 'Beeswax', 'Silicone caulk', 'Nothing — leave them as-is'], correctIndex: 1 },
      { id: 'pool-q4', questionText: 'How do you verify the pool table is properly leveled?', options: ['Eyeball it from across the room', 'Place a ball at center — it should not drift in any direction', 'Check only the rail height', 'Leveling is approximate and does not need to be precise'], correctIndex: 1 },
      { id: 'pool-q5', questionText: 'What should the felt surface have after installation?', options: ['A few minor wrinkles are acceptable', 'No wrinkles or bubbles — stretched evenly across the slate', 'Loose fit for easier replacement', 'Glued directly to the slate'], correctIndex: 1 },
    ],
  },
  {
    id: 'course-massage-chair',
    _sortOrder: 29,
    title: 'Massage Chair Delivery & Setup',
    categorySlug: 'massage-chair',
    status: 'PUBLISHED',
    videoUrl: null,
    textContent: `Guide to delivering and setting up luxury massage chairs and wellness recliners.

Key topics:
• Weight and dimensions — massage chairs weigh 200–350 lbs; they are bulky and top-heavy; always use a two-person team and appliance dolly
• Packaging removal — chairs often ship in large crates; carefully remove all packaging and internal shipping bolts/brackets before use
• Placement requirements — most massage chairs need 1–3 feet of clearance behind them for full recline; verify wall clearance before powering on
• Electrical requirements — plug into a standard 120V / 15A outlet; no extension cords; GFCI recommended for safety
• Shipping bolt removal — many massage chairs have internal shipping bolts that lock the mechanism during transport; these MUST be removed before first use or the motors will be damaged
• Initial calibration — power on and run the automatic body scan / calibration cycle; verify all rollers, airbags, and heating elements function
• Customer walkthrough — demonstrate all programs, intensity settings, zero-gravity positions, remote control operation, and auto-shutoff features`,
    passingScore: 80,
    _count: { quizQuestions: 5 },
    quizQuestions: [
      { id: 'mass-q1', questionText: 'What must be removed from a massage chair before first use?', options: ['Nothing special', 'Internal shipping bolts and brackets — or the motors may be damaged', 'The seat cushion for calibration', 'Only the packaging'], correctIndex: 1 },
      { id: 'mass-q2', questionText: 'How much clearance is typically needed behind a massage chair?', options: ['None — push it against the wall', '1–3 feet for full recline clearance', '6 feet minimum', '10 feet for safety'], correctIndex: 1 },
      { id: 'mass-q3', questionText: 'What should be run after first powering on a massage chair?', options: ['A 60-minute stress test', 'The automatic body scan / calibration cycle', 'Nothing — it is ready to use', 'Only the heating elements'], correctIndex: 1 },
      { id: 'mass-q4', questionText: 'What is the typical weight range of a massage chair?', options: ['50–100 lbs', '100–150 lbs', '200–350 lbs', '500+ lbs'], correctIndex: 2 },
      { id: 'mass-q5', questionText: 'What type of electrical outlet should a massage chair be plugged into?', options: ['Any available outlet with an extension cord', 'Standard 120V / 15A outlet — no extension cords, GFCI recommended', 'A dedicated 240V circuit', 'A power strip is fine'], correctIndex: 1 },
    ],
  },
  {
    // PALLETIZING CERTIFICATION (owner, 3 Nov). Same id, question ids and text as the backend's
    // graded course (backend lib/palletizingCourse), which is what grades it and grants it.
    id: 'course-palletizing',
    title: 'Palletizing Certification',
    categorySlug: 'palletizing',
    _sortOrder: 1,
    status: 'PUBLISHED',
    videoUrl: null,
    textContent: `Every freight item travels on a pallet through carrier terminals, trucks and forklifts. How you pack it decides whether it arrives in one piece. Read this before the quiz.

1. Buy before you go
Every palletize job lists exactly what to buy for that item. Buy all of it before the pickup:
• 1 solid wood pallet, at least as big as the item, no cracked or missing boards
• Clear stretch wrap, 18 in wide (1 or 2 rolls of 1,500 ft)
• Bubble wrap, 24 in wide (the job says how many feet)
• Large flat cardboard sheets or flattened boxes (the job says how many)
• 8 cardboard corner guards
• 2 ratchet straps
• 2 rolls of packing tape, a thick marker and a zip bag for bolts
Home Depot, Lowe's and U-Haul carry all of it. It is included in your pay for the job.

2. Photograph it first
At the seller, before anything is wrapped: all 4 sides, the top and any marks.

3. Make it one solid piece
Tape every door, drawer and moving part. Bag the bolts and small parts and tape the bag to the item. Coil and tape the power cord.

4. Bubble wrap every surface
No bare spot anywhere. Two layers on screens, glass, controls and every corner. Tape every seam. Take a photo.

5. Cardboard over the whole outside
Every side and the top, no bubble wrap showing. Double the cardboard on every corner and edge, then a corner guard on each vertical edge. Tape it down. Take a photo.

6. On the pallet, no overhang
Lay 2 layers of cardboard over the whole top of the pallet first, so the boards never touch the item. Then set it on: centered, heaviest side down, the way it has to travel. Nothing hangs over the pallet edge: overhang is what a forklift breaks. Never use a pallet with cracked or missing boards.

7. Strap it down
2 ratchet straps through the pallet openings and over the top, crossed and tight. Push it: it must not slide.

8. Stretch wrap 3 to 5 times around
Start with 3 tight turns around the pallet base to lock the load to it, wrap up to the top and back down overlapping each pass by half, then 2 turns over the top.

9. Label every side
On all 4 sides and the top: the order number, THIS SIDE UP with arrows, FRAGILE, DO NOT STACK. Tape the bill of lading on two sides.

10. Photograph the finished pallet
All 4 sides, the top and one label up close. The drop-off checklist will not finish without the layer photos.

Items with their own rules
• Refrigerators: upright only, never on the back or side. Doors taped shut, DO NOT LAY DOWN on all 4 sides.
• Hot tubs: on the side on a tub cart or skid, extra cardboard over the equipment bay, cover and steps strapped separately.
• Treadmills: deck folded or lowered, console strapped, ship flat, two layers on the screen.
• Screens and mirrors (Tonal, Peloton): two layers of bubble wrap and a cardboard sheet over the face, marked GLASS.

Collecting a pallet at the terminal
Before you sign, walk around it and photograph all 4 sides. If the wrap is torn, the cardboard is crushed or a strap is loose, write DAMAGED and what you see on their paperwork before you sign. Signing clean gives up the claim.

Then take the 5 question quiz. You need 80% to pass, and you must pass before you can accept a palletize job.`,
    passingScore: 80,
    _count: { quizQuestions: 5 },
    quizQuestions: [
      { id: 'pal-q1', questionText: 'What goes on the item first?', options: ['Stretch wrap, straight onto the item', 'Cardboard on the sides that show', 'Bubble wrap on every surface, two layers on screens, glass and corners', 'Moving blankets only'], correctIndex: 2, explanation: 'Bubble wrap takes the knocks. No bare spot anywhere, two layers on anything that breaks, every seam taped.' },
      { id: 'pal-q2', questionText: 'How much of the item does the cardboard cover?', options: ['Just the front and back', 'Only the corners', 'The top only, so nothing gets stacked on it', 'The whole outside, every side and the top, doubled on every corner and edge'], correctIndex: 3, explanation: 'No bubble wrap should show. Corners and edges take the first hit, so they get two layers and a corner guard.' },
      { id: 'pal-q3', questionText: 'How should the item sit on the pallet?', options: ['Centered, heaviest side down, nothing hanging over the edge, strapped down with 2 ratchet straps', 'Pushed to one end so it is easy to grab', 'Hanging over a little so it fits a smaller pallet', 'Any way, as long as it is wrapped'], correctIndex: 0, explanation: 'An edge past the pallet is what the forklift and the next pallet hit first. Straps stop it sliding when the truck brakes.' },
      { id: 'pal-q4', questionText: 'How many times around with clear stretch wrap?', options: ['Once, top to bottom', 'Only around the middle', '3 to 5 times, starting with tight turns around the pallet base, then 2 turns over the top', 'Wrap is optional if it is boxed'], correctIndex: 2, explanation: 'Turns around the base lock the load to the pallet. Under 3 turns, the wrap tears on the first forklift move.' },
      { id: 'pal-q5', questionText: 'You collect a pallet at the terminal and the wrap is torn. What do you do?', options: ['Sign, then text ops later', 'Photograph it and write DAMAGED and what you see on their paperwork before you sign', 'Refuse it and leave', 'Rewrap it and say nothing'], correctIndex: 1, explanation: "Signing clean gives up the claim. Note it on the carrier's paperwork at the dock, then text us the photos." },
    ],
  },
];

export const MOCK_MESSAGES = [
  { id: 'm1', messageText: 'Hi! I\'m on my way to pick up your item.', isInbound: false, recipientType: 'SELLER', senderType: 'DRIVER', timestamp: new Date(Date.now() - 3600000).toISOString() },
  { id: 'm2', messageText: 'Great! The loading dock is on the east side of the building.', isInbound: true, recipientType: 'DRIVER', senderType: 'SELLER', timestamp: new Date(Date.now() - 3000000).toISOString() },
];

// ─── JotForm-Accurate Checklist Items ────────────────────────────────────────
// Source: https://form.jotform.com/243446964144058

export interface ChecklistItem {
  id: string;
  label: string;
  type: 'checkbox' | 'photo' | 'video' | 'text' | 'select';
  required: boolean;
  options?: string[];
  helpText?: string;
}

/** Category-specific pickup inspection items — 2-3 per category for thorough inspection */
const PICKUP_CATEGORY_ITEMS: Record<string, ChecklistItem[]> = {
  'fitness-equipment': [
    { id: 'pk-cat-1', label: 'All accessories included (power cord, tools, manual)', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-2', label: 'Frame is stable with no loose bolts or wobble', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-3', label: 'Screen/display powers on (if applicable)', type: 'checkbox' as const, required: false, helpText: 'Test if the item has an electronic display — skip for non-powered equipment' },
  ],
  'hot-tub': [
    { id: 'pk-cat-1', label: 'Shell has no cracks, chips, or delamination', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-2', label: 'All jets present and drain plugs in place', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-3', label: 'Cover included and in acceptable condition', type: 'checkbox' as const, required: true },
  ],
  'sauna': [
    { id: 'pk-cat-1', label: 'All panels present and undamaged', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-2', label: 'Heating element and control panel intact', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-3', label: 'Door glass (if any) has no cracks', type: 'checkbox' as const, required: false },
  ],
  'tonal': [
    // Arrival-doable: the bracket stays on the wall until removal, so the driver
    // confirms with the seller that the mounting hardware exists (rather than
    // photographing it, which can only happen after the unit is unmounted).
    { id: 'pk-cat-1', label: 'Seller confirms original wall mount bracket and hardware available', type: 'checkbox' as const, required: true, helpText: 'Ask the seller — the buyer needs it to reinstall. Leave the arms ATTACHED; the unit comes off the wall as one piece.' },
    { id: 'pk-cat-2', label: 'Both arms extend/retract smoothly with no frayed cables', type: 'checkbox' as const, required: true, helpText: 'Pull both arms out fully and test smooth, equal resistance. Inspect cables for fraying — a safety issue.' },
    { id: 'pk-cat-3', label: 'Screen powers on and is intact with no cracks', type: 'checkbox' as const, required: true, helpText: 'Power on the unit on the wall — confirm the screen, touch, sound, and both arms work before anything is moved.' },
  ],
  'appliance': [
    { id: 'pk-cat-1', label: 'Doors, drawers, and hinges open/close freely', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-2', label: 'Power cord intact with no exposed wires', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-3', label: 'No dents on visible surfaces (front, sides, top)', type: 'checkbox' as const, required: true },
  ],
  'furniture': [
    { id: 'pk-cat-1', label: 'No scratches, dents, or stains on surfaces', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-2', label: 'All hardware bags and assembly pieces present', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-3', label: 'Structural joints are solid — no wobble or looseness', type: 'checkbox' as const, required: true },
  ],
  'pool-table': [
    { id: 'pk-cat-1', label: 'All slate pieces counted and accounted for', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-2', label: 'Felt/cloth in good condition — no tears or stains', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-3', label: 'Rails, pockets, legs, and hardware all present', type: 'checkbox' as const, required: true },
  ],
  'gym-equipment': [
    { id: 'pk-cat-1', label: 'All boxes and pieces match the packing list', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-2', label: 'Cables, pulleys, and weight stacks intact', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-3', label: 'Frame has no bends, cracks, or weld issues', type: 'checkbox' as const, required: true },
  ],
  'electronics': [
    { id: 'pk-cat-1', label: 'Screen intact — no cracks, dead pixels, or damage', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-2', label: 'Remote, power cable, and listed cables included', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-3', label: 'Device powers on when tested', type: 'checkbox' as const, required: false, helpText: 'Test power-on if possible — skip if no power outlet available' },
  ],
  'golf-simulator': [
    { id: 'pk-cat-1', label: 'Projector, launch monitor, and impact screen present', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-2', label: 'Enclosure frame pieces counted and undamaged', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-3', label: 'All cables, mounts, and accessories included', type: 'checkbox' as const, required: true },
  ],
  'golf-cart': [
    { id: 'pk-cat-1', label: 'Charger and key(s) included', type: 'checkbox' as const, required: true, helpText: 'The buyer needs the charger and at least one key.' },
    { id: 'pk-cat-2', label: 'Batteries hold a charge; no corrosion on terminals', type: 'checkbox' as const, required: true, helpText: 'Check the battery bank — corrosion or a dead pack is a major issue to document.' },
    { id: 'pk-cat-3', label: 'Tires, brakes, and seats in usable condition', type: 'checkbox' as const, required: false },
  ],
  'massage-chair': [
    { id: 'pk-cat-1', label: 'Upholstery intact — no tears, cracks, or peeling', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-2', label: 'Remote control and power cord included', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-3', label: 'Chair reclines and adjusts properly', type: 'checkbox' as const, required: false, helpText: 'Test reclining mechanism if possible' },
  ],
  'treadmill': [
    { id: 'pk-cat-1', label: 'Belt in good condition — no fraying or misalignment', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-2', label: 'Safety key/clip present and attached', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-3', label: 'Incline motor and console function (if testable)', type: 'checkbox' as const, required: false },
  ],
  'rowing-machine': [
    { id: 'pk-cat-1', label: 'Rail is straight and seat slides smoothly', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-2', label: 'Handle, chain/strap, and resistance mechanism intact', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-3', label: 'Monitor/display present and powers on', type: 'checkbox' as const, required: false },
  ],
  'cold-plunge': [
    { id: 'pk-cat-1', label: 'Tub shell intact — no cracks or leaks', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-2', label: 'Chiller/pump unit present and connected', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-3', label: 'Drain valve and hose fittings in place', type: 'checkbox' as const, required: true },
  ],
  'outdoor': [
    { id: 'pk-cat-1', label: 'All lumber, panels, and hardware packages counted', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-2', label: 'No water damage, rot, or warping on wood pieces', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-3', label: 'Assembly instructions or manual included', type: 'checkbox' as const, required: false },
  ],
  'peloton-bike': [
    { id: 'pk-cat-1', label: 'Screen/tablet is intact with no cracks', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-2', label: 'Pedals, seat, and handlebars all present and secure', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-3', label: 'Power cord and adapter included', type: 'checkbox' as const, required: true },
  ],
  'peloton-tread': [
    { id: 'pk-cat-1', label: 'Belt in good condition and runs straight', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-2', label: 'Screen/tablet intact with no cracks', type: 'checkbox' as const, required: true },
    { id: 'pk-cat-3', label: 'Safety key, power cord, and side rails present', type: 'checkbox' as const, required: true },
  ],
};

/** Category-specific delivery verification items — 2-3 per category */
const DELIVERY_CATEGORY_ITEMS: Record<string, ChecklistItem[]> = {
  'fitness-equipment': [
    { id: 'dk-cat-1', label: 'Equipment placed on level surface', type: 'checkbox' as const, required: true },
    { id: 'dk-cat-2', label: 'Powers on and functions correctly', type: 'checkbox' as const, required: false, helpText: 'Test power and basic functions if outlet is available' },
  ],
  'hot-tub': [
    { id: 'dk-cat-1', label: 'Placed on reinforced, level surface', type: 'checkbox' as const, required: true, helpText: 'Hot tubs are extremely heavy when filled — surface must support the weight' },
    { id: 'dk-cat-2', label: 'Cover fits properly and is in place', type: 'checkbox' as const, required: true },
  ],
  'sauna': [
    { id: 'dk-cat-1', label: 'All panels assembled and properly aligned', type: 'checkbox' as const, required: true },
    { id: 'dk-cat-2', label: 'Door closes and latches correctly', type: 'checkbox' as const, required: true },
  ],
  'tonal': [
    { id: 'dk-cat-1', label: 'Wall mount installed securely on appropriate surface', type: 'checkbox' as const, required: true },
    { id: 'dk-cat-2', label: 'Arms extend and retract smoothly', type: 'checkbox' as const, required: true },
  ],
  'appliance': [
    { id: 'dk-cat-1', label: 'Placed in designated spot and leveled', type: 'checkbox' as const, required: true },
    { id: 'dk-cat-2', label: 'Connected to power — doors/drawers open freely', type: 'checkbox' as const, required: false, helpText: 'Connect to power if customer requests — verify basic operation' },
  ],
  'furniture': [
    { id: 'dk-cat-1', label: 'Assembled (if required) and structurally stable', type: 'checkbox' as const, required: true },
    { id: 'dk-cat-2', label: 'All drawers, doors, and mechanisms work smoothly', type: 'checkbox' as const, required: true },
  ],
  'pool-table': [
    { id: 'dk-cat-1', label: 'Slate pieces set and leveled', type: 'checkbox' as const, required: true },
    { id: 'dk-cat-2', label: 'Felt secured and rails properly attached', type: 'checkbox' as const, required: true },
    { id: 'dk-cat-3', label: 'Legs stable and table does not rock', type: 'checkbox' as const, required: true },
  ],
  'gym-equipment': [
    { id: 'dk-cat-1', label: 'Frame fully assembled and stable', type: 'checkbox' as const, required: true },
    { id: 'dk-cat-2', label: 'Cables, pulleys, and weight stacks move freely', type: 'checkbox' as const, required: true },
  ],
  'electronics': [
    { id: 'dk-cat-1', label: 'Device powers on and displays correctly', type: 'checkbox' as const, required: true },
    { id: 'dk-cat-2', label: 'All cables connected and remote paired', type: 'checkbox' as const, required: false },
  ],
  'golf-simulator': [
    { id: 'dk-cat-1', label: 'Enclosure fully assembled and secured', type: 'checkbox' as const, required: true },
    { id: 'dk-cat-2', label: 'Projector aligned and launch monitor positioned', type: 'checkbox' as const, required: true },
  ],
  'massage-chair': [
    { id: 'dk-cat-1', label: 'Powers on and massage programs run correctly', type: 'checkbox' as const, required: true },
    { id: 'dk-cat-2', label: 'Reclining and adjustment mechanisms work', type: 'checkbox' as const, required: true },
  ],
  'treadmill': [
    { id: 'dk-cat-1', label: 'Belt runs smoothly at multiple speeds', type: 'checkbox' as const, required: true },
    { id: 'dk-cat-2', label: 'Emergency stop and safety key tested', type: 'checkbox' as const, required: true },
  ],
  'rowing-machine': [
    { id: 'dk-cat-1', label: 'Seat slides freely on rail', type: 'checkbox' as const, required: true },
    { id: 'dk-cat-2', label: 'Resistance adjustable and handle pulls smoothly', type: 'checkbox' as const, required: true },
  ],
  'cold-plunge': [
    { id: 'dk-cat-1', label: 'Placed on level surface with drainage access', type: 'checkbox' as const, required: true },
    { id: 'dk-cat-2', label: 'Chiller/pump connected and running with no leaks', type: 'checkbox' as const, required: true },
  ],
  'outdoor': [
    { id: 'dk-cat-1', label: 'Structure fully assembled and level', type: 'checkbox' as const, required: true },
    { id: 'dk-cat-2', label: 'Anchored/secured per manufacturer guidelines', type: 'checkbox' as const, required: true },
  ],
  'peloton-bike': [
    { id: 'dk-cat-1', label: 'Bike is level and stable on floor', type: 'checkbox' as const, required: true },
    { id: 'dk-cat-2', label: 'Screen powers on and seat/handlebars adjust', type: 'checkbox' as const, required: true },
  ],
  'peloton-tread': [
    { id: 'dk-cat-1', label: 'Tread is level and belt runs straight', type: 'checkbox' as const, required: true },
    { id: 'dk-cat-2', label: 'Screen powers on and safety key tested', type: 'checkbox' as const, required: true },
  ],
};

/**
 * Generate category-specific checklist items based on job data.
 *
 * PICKUP PURPOSE: Verify the item matches the order listing, document its
 * condition at the seller's location, and confirm it's ready for transport.
 * "Photo of item loaded in vehicle" is a separate step AFTER seller signature.
 *
 * DELIVERY PURPOSE: Confirm correct delivery, document final placement,
 * perform category-specific verification, and obtain customer satisfaction.
 */
export function getChecklistItems(type: 'pickup' | 'delivery', job: any): ChecklistItem[] {
  const slug = job?.categorySlug || '';

  if (type === 'pickup') {
    const categoryItems = PICKUP_CATEGORY_ITEMS[slug] || [];

    // Categories that have electronic screens/displays
    const hasScreen = ['peloton-bike', 'peloton-bike-plus', 'peloton-tread', 'peloton-tread-plus', 'peloton-row', 'tonal', 'treadmill', 'electronics', 'massage-chair'].includes(slug);
    // Categories that have model/serial labels worth photographing
    const hasSerialLabel = ['peloton-bike', 'peloton-bike-plus', 'peloton-tread', 'peloton-tread-plus', 'peloton-row', 'tonal', 'treadmill', 'rowing-machine', 'appliance', 'electronics', 'gym-equipment', 'massage-chair', 'cold-plunge', 'hot-tub'].includes(slug);
    // Categories where "all parts/accessories" check makes sense
    const hasParts = !['furniture', 'pool-table', 'outdoor'].includes(slug);
    // Furniture/decor: focus on surface condition instead of functional checks
    const isFurnitureOrDecor = ['furniture'].includes(slug) || (job?.productTitle || '').toLowerCase().match(/mirror|table|shelf|bookcase|cabinet|dresser|desk|chair|sofa|couch|bench|stool|nightstand|credenza|console|buffet|hutch/);

    // Build condition options based on category
    const conditionOptions = isFurnitureOrDecor
      ? ['Excellent — no visible marks', 'Good — minor surface wear', 'Fair — noticeable scratches or stains', 'Damaged — document below']
      : hasScreen
        ? ['Excellent — like new', 'Good — minor cosmetic wear', 'Fair — visible wear but functional', 'Damaged — document below']
        : ['Excellent — like new', 'Good — minor wear', 'Fair — visible wear', 'Damaged — document below'];

    const items: ChecklistItem[] = [
      // ── Section 1: Listing Verification ──
      { id: 'pk-match', label: 'Item matches listing photos and description', type: 'checkbox', required: true, helpText: 'Compare the physical item against the listing photos — verify make, model, color, and size' },
    ];

    if (hasParts) {
      items.push({ id: 'pk-parts', label: 'All listed parts, accessories, and components present', type: 'checkbox', required: true, helpText: 'Check for power cords, remotes, hardware bags, manuals, and any extras mentioned in the listing' });
    }

    // ── Section 2: Condition Documentation ──
    items.push({ id: 'pk-condition', label: 'Overall item condition', type: 'select', required: true, options: conditionOptions });

    if (isFurnitureOrDecor) {
      items.push(
        { id: 'pk-photo-front', label: 'Photo — front/main surface', type: 'photo', required: true, helpText: 'Clear photo of the main visible surface showing finish and condition' },
        { id: 'pk-photo-back', label: 'Photo — back/underside', type: 'photo', required: true, helpText: 'Show back, underside, or hidden sides to document full condition' },
        { id: 'pk-photo-detail', label: 'Close-up of any scratches, chips, or stains', type: 'photo', required: false, helpText: 'Skip if item is in perfect condition' },
      );
    } else {
      items.push(
        { id: 'pk-photo-front', label: 'Photo of item — front view', type: 'photo', required: true, helpText: 'Clear, well-lit photo showing the full item from the front' },
        { id: 'pk-photo-back', label: 'Photo of item — back/side view', type: 'photo', required: true, helpText: 'Show the back or opposite side to document full condition' },
      );
      if (hasSerialLabel) {
        items.push({ id: 'pk-photo-label', label: 'Photo of model/serial label', type: 'photo', required: true, helpText: 'Capture the model number, serial number, or brand tag' });
      }
      items.push({ id: 'pk-photo-damage', label: 'Close-up of any existing damage or wear', type: 'photo', required: false, helpText: 'Skip if item is in perfect condition' });
    }

    // ── Section 3: Category-Specific Inspection ──
    items.push(...categoryItems);

    // Titled vehicles (golf carts / LSVs / watercraft / trailers): require a photo of the
    // signed title + a short running/working video — mirrors the backend live checklist so
    // this fallback (used only if the /checklists fetch fails) keeps the vehicle gate.
    const vt = (job?.productTitle || '').toLowerCase();
    const isTitledVehicle = ['golf-cart', 'watercraft', 'powersports', 'trailer', 'vehicle'].includes(slug)
      || /golf ?cart|club ?car|ez-?go|e-z-?go|yamaha drive|advanced ?ev|\blsv\b|sea-?doo|jet ?ski|waverunner|watercraft|trailer|atv|utv|moped|scooter|motorcycle|boat/.test(vt);
    if (isTitledVehicle) {
      items.push(
        { id: 'pk-photo-title', label: 'Photo — signed title / certificate of ownership', type: 'photo', required: true, helpText: 'Clear photo of the signed title or certificate of ownership. We need this on file before transport.' },
        { id: 'pk-video-running', label: 'Video — vehicle running & working', type: 'video', required: true, helpText: '5–15 sec: start it and show it powers on, moves, and brakes.' },
      );
    }

    // ── Section 4: Transport Readiness ──
    // Arrival-doable only: confirming fit is something the driver can assess the
    // moment they arrive. The "wrapped/protected for transport" photo was removed
    // because it can only be taken AFTER the item is disassembled/loaded — that
    // step lives in the lightbulb guide (driverTips), not the pre-disassembly
    // pickup checklist (so the seller can be paid before any disassembly).
    items.push(
      { id: 'pk-dimensions', label: 'Item fits safely in vehicle for transport', type: 'checkbox', required: true, helpText: 'Confirm the item can be secured properly in your vehicle' },
    );

    // ── Section 5: Seller Confirmation ──
    items.push(
      { id: 'pk-seller-present', label: 'Seller present and confirmed release of item', type: 'checkbox', required: true },
      { id: 'pk-notes', label: 'Pickup notes', type: 'text', required: false, helpText: 'Note any concerns, special handling instructions, or discrepancies with listing' },
    );

    return items;
  }

  // ── DELIVERY CHECKLIST ──
  const categoryItems = DELIVERY_CATEGORY_ITEMS[slug] || [];
  return [
    // ── Section 1: Arrival Verification ──
    { id: 'dk-address', label: 'Correct delivery address confirmed', type: 'checkbox', required: true, helpText: 'Verify the address matches what is shown in the order details above' },
    { id: 'dk-customer', label: 'Customer or authorized person present', type: 'checkbox', required: true },

    // ── Section 2: Unloading & Condition Check ──
    { id: 'dk-photo-unload', label: 'Photo of item being unloaded', type: 'photo', required: true, helpText: 'Show the item as it comes off the vehicle — documents condition at time of delivery' },
    { id: 'dk-transit', label: 'Item condition after transit', type: 'select', required: true, options: ['No damage — same as pickup', 'Minor scuff from transit', 'New damage occurred — document below'] },
    { id: 'dk-photo-damage', label: 'Close-up of any new transit damage', type: 'photo', required: false, helpText: 'Only needed if damage occurred during transport — skip if item is in same condition as pickup' },

    // ── Section 3: Placement & Setup ──
    { id: 'dk-placement', label: 'Item placed in customer-requested location', type: 'checkbox', required: true, helpText: 'Confirm the item is exactly where the customer wants it' },
    { id: 'dk-photo-placed', label: 'Photo of item in final position', type: 'photo', required: true, helpText: 'Show the item in its final placement at the delivery location' },
    // Category-specific (e.g. "powers on", "assembled correctly", "level surface")
    ...categoryItems,

    // ── Section 4: Completion Verification ──
    { id: 'dk-parts-delivered', label: 'All parts and accessories delivered with item', type: 'checkbox', required: true, helpText: 'Double-check nothing was left in the vehicle — cords, remotes, hardware, manuals' },
    { id: 'dk-cleanup', label: 'Packaging materials and debris cleaned up', type: 'checkbox', required: true, helpText: 'Remove all blankets, straps, cardboard, and wrapping from the delivery area' },
    { id: 'dk-walkaround', label: 'Video walkaround of delivered item', type: 'video', required: true, helpText: 'Record a short video walking around the item in its final position — shows condition and placement for our records' },
    { id: 'dk-satisfied', label: 'Customer confirmed satisfaction with delivery', type: 'checkbox', required: true },
    { id: 'dk-notes', label: 'Delivery notes', type: 'text', required: false, helpText: 'Note any concerns, customer feedback, or follow-up needed' },
  ];
}

/** @deprecated Use getChecklistItems(type, job) instead — kept for backward compatibility */
export const MOCK_CHECKLIST_ITEMS = {
  pickup: getChecklistItems('pickup', {}),
  delivery: getChecklistItems('delivery', {}),
};

// ─── Bill of Lading Template ─────────────────────────────────────────────────
// Source: JotForm checklist fields

export interface BillOfLadingData {
  customerName: string;
  sellerName: string;
  customerPhone: string;
  sellerPhone: string;
  customerEmail: string;
  sellerEmail: string;
  driverName: string;
  driverPhone: string;
  equipmentType: string;
  equipmentModel: string;
  rideCount: string;
  equipmentPrice: string;
  pickupAddress: string;
  deliveryAddress: string;
  deliveryDate: string;
  orderId: string;
  customerSignature: boolean;
  driverSignature: boolean;
}
